import { AddOrEditMatterLitigationDateTypeModal } from "@/components/features/settings/defaults/matter-litigation-date-types/AddOrEditMatterLitigationDateTypeModal";
import { MatterLitigationDateTypeRowContextMenu } from "@/components/features/settings/defaults/matter-litigation-date-types/MatterLitigationDateTypeRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { ActionConfirmationModal, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterLitigationDateType } from "@/lib/data/react-query/matters/matter-litigation-date-type";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterLitigationDateType } from "@/lib/types";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

const LITIGATION_DATE_TYPE_TABLE_COLUMN_IDS = [
  "Label",
  "Category",
  "Description",
] as const;

const MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY: string =
  tanstackTableNames.matterLitigationDateTypes;

function MatterLitigationDateTypesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDateType,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDateType,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [activeLitigationDateTypeId, setActiveLitigationDateTypeId] = useState<
    string | null
  >(null);
  const [dateTypeToDelete, setDateTypeToDelete] =
    useState<MatterLitigationDateType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deleteMatterLitigationDateType = useDeleteMatterLitigationDateType();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(
      MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY,
    );
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(
      MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY,
    );
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(
      MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY,
    );
    const ids = [...LITIGATION_DATE_TYPE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(
        MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY,
      );
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const matterLitigationDateTypes = useMemo(
    () => matterSettingsData?.data.litigationDateTypes ?? [],
    [matterSettingsData?.data.litigationDateTypes],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matterLitigationDateTypes;
    return matterLitigationDateTypes.filter((row) => {
      const label = row.label.toLowerCase();
      const category = row.category.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      return (
        label.includes(q) || category.includes(q) || description.includes(q)
      );
    });
  }, [matterLitigationDateTypes, searchQuery]);

  const columns = useMemo<ColumnDef<MatterLitigationDateType>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => row.original.label,
        sortingFn: "alphanumeric",
      },
      {
        id: "Category",
        accessorFn: (row) => row.category,
        header: "Category",
        cell: ({ row }) => row.original.category,
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => row.original.description ?? "-",
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_LITIGATION_DATE_TYPES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const activeLitigationDateType = matterLitigationDateTypes.find(
    (type) => type.id === activeLitigationDateTypeId,
  );

  const handleDeleteDateType = async () => {
    if (!dateTypeToDelete) return;

    await deleteMatterLitigationDateType.mutateAsync({
      id: dateTypeToDelete.id,
    });
    setDateTypeToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={LITIGATION_DATE_TYPE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search types...",
        }}
        isLoading={isMatterSettingsLoading}
        isError={isMatterSettingsError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No litigation date types found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="types"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterLitigationDateTypes,
          "data-table-name": tanstackTableNames.matterLitigationDateTypes,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveLitigationDateTypeId(row.original.id);
            setIsEditing(true);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterLitigationDateTypeRowContextMenu
            dateType={row.original}
            trigger={trigger}
            canEdit={hasUpdatePermission}
            canDelete={hasDeletePermission}
            onEdit={() => {
              setActiveLitigationDateTypeId(row.original.id);
              setIsEditing(true);
            }}
            onDelete={() => {
              setDateTypeToDelete(row.original);
            }}
          />
        )}
      />

      {activeLitigationDateTypeId && activeLitigationDateType ? (
        <AddOrEditMatterLitigationDateTypeModal
          editing
          open={isEditing}
          setOpen={setIsEditing}
          activeMatterLitigationDateType={activeLitigationDateType}
          isReadOnly={!hasUpdatePermission || !hasDeletePermission}
        />
      ) : null}

      <AddOrEditMatterLitigationDateTypeModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
      />

      <ActionConfirmationModal
        open={Boolean(dateTypeToDelete)}
        setOpen={(open) => {
          if (!open) setDateTypeToDelete(null);
        }}
        title="Delete litigation date type"
        description={`Are you sure you want to delete "${dateTypeToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteDateType}
      />
    </div>
  );
}

export function MatterLitigationDateTypesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterLitigationDateTypesTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
